import React from 'react';
import styled from 'styled-components';

const StepContainer = styled.div`
  padding: 2rem;
`;

const StepContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const SectionTitle = styled.h3`
  color: #333;
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const OrderDetails = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const OrderMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const MetaItem = styled.div`
  .label {
    font-size: 0.8rem;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
    font-weight: 600;
  }
  
  .value {
    font-size: 1rem;
    color: #333;
    font-weight: 600;
  }
`;

const ItemsList = styled.div`
  border-top: 1px solid #e9ecef;
  padding-top: 1.5rem;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  
  .name {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.25rem;
  }
  
  .event {
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }
  
  .details {
    font-size: 0.8rem;
    color: #666;
  }
`;

const ItemQuantity = styled.div`
  margin: 0 1rem;
  text-align: center;
  
  .quantity {
    font-weight: 600;
    color: #333;
    font-size: 1.1rem;
  }
  
  .label {
    font-size: 0.8rem;
    color: #666;
  }
`;

const ItemPrice = styled.div`
  text-align: right;
  
  .price {
    font-weight: 600;
    color: #333;
    font-size: 1.1rem;
  }
  
  .unit-price {
    font-size: 0.8rem;
    color: #666;
  }
`;

const ImportantNotice = styled.div`
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 2rem;
  
  .title {
    font-weight: 600;
    color: #1565c0;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .content {
    font-size: 0.9rem;
    color: #1976d2;
    line-height: 1.4;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #666;
    border: 1px solid #e9ecef;
    
    &:hover {
      background: #e9ecef;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #666;
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    margin-bottom: 0.5rem;
    color: #333;
  }
  
  p {
    margin-bottom: 2rem;
  }
`;

const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const OrderReviewStep = ({ order, onNext }) => {
    if (!order || !order.items || order.items.length === 0) {
        return (
            <StepContainer>
                <EmptyState>
                    <div className="icon">📋</div>
                    <h3>No Order Found</h3>
                    <p>We couldn't find any items in your order. Please go back and try again.</p>
                    <Button
                        className="secondary"
                        onClick={() => window.history.back()}
                    >
                        Go Back
                    </Button>
                </EmptyState>
            </StepContainer>
        );
    }

    const handleContinue = () => {
        onNext();
    };

    return (
        <StepContainer>
            <StepContent>
                <SectionTitle>Review Your Order</SectionTitle>

                <OrderDetails>
                    <OrderMeta>
                        <MetaItem>
                            <div className="label">Order Number</div>
                            <div className="value">{order.orderNumber}</div>
                        </MetaItem>
                        <MetaItem>
                            <div className="label">Total Items</div>
                            <div className="value">{order.items.length} ticket type(s)</div>
                        </MetaItem>
                        <MetaItem>
                            <div className="label">Total Amount</div>
                            <div className="value">${order.totalAmount.toFixed(2)}</div>
                        </MetaItem>
                    </OrderMeta>

                    <ItemsList>
                        {order.items.map((item, index) => (
                            <OrderItem key={item.id || index}>
                                <ItemInfo>
                                    <div className="name">{item.ticketTypeName}</div>
                                    <div className="event">{item.eventTitle}</div>
                                    <div className="details">
                                        {formatDate(item.eventDate)} • {item.venueName}
                                    </div>
                                </ItemInfo>
                                <ItemQuantity>
                                    <div className="quantity">{item.quantity}</div>
                                    <div className="label">qty</div>
                                </ItemQuantity>
                                <ItemPrice>
                                    <div className="price">${item.salePrice.toFixed(2)}</div>
                                    <div className="unit-price">
                                        ${item.unitPrice.toFixed(2)} each
                                    </div>
                                </ItemPrice>
                            </OrderItem>
                        ))}
                    </ItemsList>
                </OrderDetails>

                <ImportantNotice>
                    <div className="title">
                        <span>⏰</span>
                        <span>Important Notice</span>
                    </div>
                    <div className="content">
                        Your tickets are reserved for 10 minutes. Please complete your purchase
                        within this time to secure your tickets. You can see the remaining time
                        in the sidebar.
                    </div>
                </ImportantNotice>

                <ActionButtons>
                    <Button
                        className="primary"
                        onClick={handleContinue}
                    >
                        Continue to Customer Info
                    </Button>
                </ActionButtons>
            </StepContent>
        </StepContainer>
    );
};

export default OrderReviewStep;
